/**
 * Formate la date d'une session Chabbat comme une plage vendredi/samedi.
 *
 * La date enregistrée peut basculer sur le dimanche selon le fuseau qui la lit.
 * On conserve donc la règle historique du module : caler d'abord l'instant sur
 * le samedi le plus proche en UTC, puis en déduire le vendredi précédent.
 * Le fuseau du visiteur ne change ainsi ni les jours ni le format de la plage.
 */
export function formatShabbatSessionDateRange(value, locale, format = 'long') {
    if (!value) return "";

    const saturday = new Date(value);
    if (isNaN(saturday.getTime())) return "";

    const day = saturday.getUTCDay(); // 0=dim … 6=sam
    let delta = 6 - day;
    if (delta > 3) delta -= 7;
    if (delta < -3) delta += 7;
    saturday.setUTCDate(saturday.getUTCDate() + delta);

    const friday = new Date(saturday);
    friday.setUTCDate(friday.getUTCDate() - 1);

    // Le code interne "gr" n'est pas une locale BCP-47 valide.
    const intlLocale = locale === "gr" ? "el" : (locale || "fr");
    const sameYear = friday.getUTCFullYear() === saturday.getUTCFullYear();
    const sameMonthAndYear = sameYear && friday.getUTCMonth() === saturday.getUTCMonth();

    // Le format numérique est explicitement jour/mois/année, même en anglais.
    if (format === 'numeric') {
        const day = date => String(date.getUTCDate()).padStart(2, '0');
        const month = date => String(date.getUTCMonth() + 1).padStart(2, '0');
        const full = date => `${day(date)}/${month(date)}/${date.getUTCFullYear()}`;
        const start = sameMonthAndYear ? day(friday)
            : sameYear ? `${day(friday)}/${month(friday)}` : full(friday);
        return `${start}–${full(saturday)}`;
    }

    if (format === 'month') {
        const formatter = new Intl.DateTimeFormat(intlLocale, {
            day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC', calendar: 'gregory',
        });
        return typeof formatter.formatRange === 'function'
            ? formatter.formatRange(friday, saturday)
            : `${formatter.format(friday)} – ${formatter.format(saturday)}`;
    }

    // Une valeur absente ou inconnue conserve le rendu historique.
    const fullFormatter = new Intl.DateTimeFormat(intlLocale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: 'UTC',
        calendar: 'gregory',
    });

    const fullParts = fullFormatter.formatToParts(saturday);
    const monthAfterDay = fullParts.findIndex(part => part.type === "month")
        > fullParts.findIndex(part => part.type === "day");

    // Dans les langues où le mois suit le jour, on évite de le répéter :
    // "vendredi 5 et samedi 6 janvier 2024". Les locales à ordre mois/jour
    // gardent deux dates complètes pour préserver une formulation naturelle.
    const formattedDates = sameMonthAndYear && monthAfterDay
        ? [
            new Intl.DateTimeFormat(intlLocale, {
                weekday: "long",
                day: "numeric",
                timeZone: 'UTC',
                calendar: 'gregory',
            }).format(friday),
            fullFormatter.format(saturday),
        ]
        : [fullFormatter.format(friday), fullFormatter.format(saturday)];

    if (typeof Intl.ListFormat === "function") {
        return new Intl.ListFormat(intlLocale, {
            style: "long",
            type: "conjunction",
        }).format(formattedDates);
    }

    if (typeof fullFormatter.formatRange === "function") {
        return fullFormatter.formatRange(friday, saturday);
    }

    return formattedDates.join(" – ");
}
